import React from 'react';
import styled from 'styled-components';

import { AddressBar, Counter, Marquee, Mast, StatusBar, WEB, xpCss, webCss } from 'lib/xp';
import { EXPLORER } from 'data/msft';
import { fmtAmt, toAmount } from 'data/epochs';
import { useLedger } from 'data/ledger';

import pfp from 'assets/pfp.png';

// DIVIDENDS.exe — the payout ledger, dressed as a 2001 web page inside a Windows frame.
// Restyled 2026-09-13 onto lib/xp's `web` layer (red — this is a statement of account, and
// ledger red is the oldest colour in bookkeeping). The beige pass this replaces was, in Kris's
// words, "gray, boring"; the readability rule it also broke is dark ink on light, light ink on
// dark, never ink on ink.
//
// Reads pons' per-token distributor epochs (data/dividends.json, written by engine/tools from
// chain reads). pons' keeper batch-claims every epoch for every holder within minutes, so each
// row is rendered as "paid to holders". Chain-derived numbers only, never hand-written.

const short = a => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : null);
const when = iso => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default function Dividends() {
  const { asset, distributor, epochs, paidTotal, pendingAmount, sharingPending, updatedAt, live } = useLedger();
  return (
    <Div>
      <div className="xp__menubar">
        <button type="button">File</button>
        <button type="button">Edit</button>
        <button type="button">View</button>
        <button type="button">Help</button>
        <span className="xp__menubar__brand">Internet Coin Payroll</span>
      </div>
      <AddressBar path="dividends.internetcoin.vip/ledger.htm" />

      <div className="xp__client web web--red">
        <Mast
          logo={pfp}
          kicker="Internet Coin Payroll"
          title="Dividend Ledger"
          right={
            <div className="dv__epochs">
              <Counter value={epochs.length} digits={4} />
              <span className="dv__epochs__cap">EPOCHS PAID</span>
            </div>
          }
        />
        <Marquee>
          ★ every epoch on-chain ★ every receipt public ★ no promises, just receipts ★ the dev earns
          nothing from trading ★
        </Marquee>

        <div className="web__body">
          <div className="web__panel web__panel--white">
            <div className="web__panel__t">How the money reaches you</div>
            <p className="dv__lede">
              2% per trade (1% creator tax + pons&apos; 1%) → <b>1.7% of every trade</b>, in real
              tokenized {asset} → airdropped to holders by pons, every epoch.
            </p>
          </div>

          {epochs.length === 0 ? (
            <div className="web__panel web__panel--navy web__center dv__empty">
              <div className="dv__empty__icon">✉</div>
              <div className="dv__empty__line">No epochs yet.</div>
              <div className="dv__empty__sub">
                The Internet is warming up. pons publishes the first epoch after launch, once real
                fees exist — this window fills with real transactions.
              </div>
            </div>
          ) : (
            <>
              <div className="web__cols dv__totals">
                <div className="web__panel web__panel--felt web__center">
                  <div className="web__panel__t">Paid to holders</div>
                  <span className="web__led web__led--big">
                    {fmtAmt(paidTotal)} {asset}
                  </span>
                </div>
                <div className="web__panel web__panel--navy web__center">
                  <div className="web__panel__t">Accrued, not yet published</div>
                  <span className="web__led web__led--big">
                    {fmtAmt(pendingAmount)} {asset}
                  </span>
                </div>
              </div>

              <div className="web__panel web__panel--navy">
                <div className="web__panel__t">Every epoch, newest first</div>
                <table className="web__table dv__table">
                  <thead>
                    <tr>
                      <th>Epoch</th>
                      <th>Published</th>
                      <th>Paid to holders</th>
                      <th>Claimed</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...epochs].reverse().map(e => {
                      const total = toAmount(e.total);
                      const claimed = toAmount(e.claimed);
                      const pct = total > 0 ? Math.min(100, Math.round((claimed / total) * 100)) : 0;
                      const settled = !e.rolledOver && pct >= 100;
                      return (
                        <tr key={e.id} className={settled ? 'is-done' : ''}>
                          <td>#{e.id}</td>
                          <td>{when(e.publishedAt)}</td>
                          <td>
                            {fmtAmt(total)} {asset}
                          </td>
                          <td>{pct}%</td>
                          <td>{e.rolledOver ? 'rolled over' : settled ? 'settled ✓' : 'claiming'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="web__hr" />

          <div className="web__foot">
            Paid by:{' '}
            {distributor ? (
              <a href={`${EXPLORER}/address/${distributor}`} target="_blank" rel="noreferrer noopener">
                pons distributor {short(distributor)} ↗
              </a>
            ) : sharingPending ? (
              <b>holder fee sharing — activating (distributor not yet registered)</b>
            ) : (
              <b>pons&apos; distributor, automatically</b>
            )}{' '}
            · dev earns nothing from trading ·{' '}
            {updatedAt
              ? `${live ? 'live from chain · ' : 'last reading · '}${when(updatedAt)}`
              : 'CA: 0x88888cC1c17FfcBa563F418c61596b3fa2Ca2e2c'}
          </div>
        </div>
      </div>

      <StatusBar>
        <span>{epochs.length ? `${epochs.length} epochs` : 'Ready'}</span>
        <span>{live ? 'Live from chain' : 'Cached'}</span>
        <span>Internet</span>
      </StatusBar>
    </Div>
  );
}

const Div = styled.div`
  ${xpCss};
  ${webCss};
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 0;
  }

  /* the epoch odometer in the masthead */
  .dv__epochs {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }
  .dv__epochs__cap {
    font: bold 9px Tahoma, sans-serif;
    letter-spacing: 2px;
    color: ${WEB.gold};
  }

  .dv__lede {
    margin: 0;
    line-height: 1.5;
  }

  /* Totals sit on dark panels, so the LED plate carries them — lime on near-black is the one
     place the brand colour survives the restyle, and it is readable there. */
  .dv__totals .web__led--big {
    max-width: 100%;
    word-break: break-word;
  }

  .dv__empty {
    padding: 22px 16px;
  }
  .dv__empty__icon {
    font-size: 34px;
    color: ${WEB.goldDeep};
    margin-bottom: 6px;
  }
  /* A web__panel is CREAM with a coloured border, not a coloured fill — so this text takes dark
     ink. Writing white here would have reproduced the exact bug this restyle exists to fix. */
  .dv__empty__line {
    font: bold 15px 'Times New Roman', Times, serif;
    color: ${WEB.navy};
    margin-bottom: 4px;
  }
  .dv__empty__sub {
    color: ${WEB.ink};
    max-width: 420px;
    margin: 0 auto;
    line-height: 1.5;
  }

  /* The ledger is numeric: right-align the money and the percentage so columns scan.
     The amount keeps full-strength ink even on a settled row: the is-done modifier tints the
     whole row green, which is right for a status word and wrong for the one number people
     opened the window to read. (No backticks in here — this block is a template literal.) */
  .dv__table td:nth-child(3),
  .dv__table td:nth-child(4) {
    text-align: right;
    font-family: 'Courier New', monospace;
  }
  .dv__table td:nth-child(3) {
    color: ${WEB.ink};
    font-weight: bold;
  }
  .dv__table th:nth-child(3),
  .dv__table th:nth-child(4) {
    text-align: right;
  }
  @media (max-width: 460px) {
    .dv__table th:nth-child(2),
    .dv__table td:nth-child(2) {
      display: none;
    }
  }
`;
